import { Avatar, Box } from "@mui/material";
import React, { createRef, useLayoutEffect, useState } from "react";
import { DotLegalChip, DotLegalHeader, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import DotLegalPaper from "../../common/components/dotLegalPaper/DotLegalPaper";
import { BusinessAreaDiplayViewModel } from "../../masterData/businessArea/businessAreas/BusinessAreas.types";
import { getTaskColor, useTaskStyles } from "./Task.styles";
import { useTranslation } from "../../localization/useTranslation";
import Calendar from "./calender.svg?react";
import Unassigned from "./unassigned.svg?react";
import CheckedCheckboxIcon from "./subTaskCompleted.svg?react";
import { TaskLaneStateEnum, TaskRawStateEnum } from "../../annualWheel/AnnualWheel.types";
import { highRiskIcon, lowRiskIcon, moderateRiskIcon, reallyHighRiskIcon, reallyLowRiskIcon } from "../../riskAssessment/riskIndicator/riskIcons";
import { AnnualWheelPriority } from "../../annualWheel/annualWheelDialog/AnnualWheelDialog.types";
import { useUrlProvider } from "../../useUrlProvider";
import DotLegalLink from "../../common/components/dotLegalLink/DotLegalLink";
import { SelectableItem } from "../../processingActivity/ProcessingActivity.types";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { TaskResponsibleViewModel } from "../TaskManagement.types";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import ComplianceAreaWrapper from "../../common/components/complianceAreaWrapper/ComplianceAreaWrapper.tsx";
import { EvaluationStatus } from "../../common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types.ts";
import TaskHeader from "./TaskHeader.tsx";
import { useLocation } from "react-router";
import TaskFooter from "./TaskFooter.tsx";

export interface ITaskProps {
    activityName: string;
    taskName: string;
    description?: string;
    businessAreas: Array<BusinessAreaDiplayViewModel>;
    responsibles: Array<TaskResponsibleViewModel>;
    deadline: Date;
    subTaskCompleted: number;
    totalSubTasks: number;
    taskStatus: TaskLaneStateEnum;
    isOverdue: boolean;
    priority: AnnualWheelPriority;
    customId?: string;
    taskId: string;
    processingActivtiyId: string | null;
    areas: Array<SelectableItem> | undefined;
    isReadyForApproval: boolean;
    isAdhocTask: boolean;
    annualWheelActivityId?: string;
    contractId?: string;
    contractName?: string;
    contractUserFriendlyId?: number;
    riskMitigationPlanId?: string | null;
    evaluationStatus?: EvaluationStatus;
    evaluation?: string;
}

export const getTaskDeadLine = (styles: any, deadline: string) => {
    return (
        <Box sx={styles.gridItemsContainer}>
            <Calendar />
            <Box sx={styles.text}>{deadline}</Box>
        </Box>
    );
};

const getReponsibleToShow = (responsibles: Array<TaskResponsibleViewModel>) => {
    return responsibles?.find((x) => x.isResponsible) ?? responsibles[0];
};

const getResponsibleName = (responsibles: Array<TaskResponsibleViewModel>, translateString: any) => {
    if (responsibles.length > 0) {
        const responsible = getReponsibleToShow(responsibles);
        let value = responsible ? `${responsible.firstName} ${responsible.lastName}` : `${responsibles[0].firstName} ${responsibles[0].lastName}`;

        return (
            <Box component={"span"}>
                {value}
                <Box component={"span"} sx={{ fontWeight: 700 }}>
                    {responsibles.length > 1 ? ` +${responsibles.length - 1}` : ""}
                </Box>
            </Box>
        );
    }

    return translateString("noResponsible");
};

export const getTaskResponsible = (responsibles: Array<TaskResponsibleViewModel>, styles: any, translateString: any) => {
    const responsible = getReponsibleToShow(responsibles);
    return (
        <Box sx={styles.gridItemsContainer}>
            {responsible ? (
                <Box sx={{ position: "relative" }}>
                    <Avatar sx={{ border: "1px solid #7284fa", backgroundColor: "#eeeef9", color: "#7284fa", height: 20, width: 20, fontSize: 11 }}>
                        {responsible.firstName.charAt(0).toUpperCase()}
                        {responsible.lastName.charAt(0).toUpperCase()}
                    </Avatar>
                </Box>
            ) : (
                <Box sx={{ display: "flex", "& svg": { height: 20, width: 20 } }}>
                    <Unassigned />
                </Box>
            )}
            <Box sx={styles.text}>{getResponsibleName(responsibles, translateString)}</Box>
        </Box>
    );
};

export const getTaskSubTasks = (styles: any, subTaskCompleted: number, totalSubTasks: number) => {
    return (
        <Box sx={styles.gridItemsContainer}>
            <CheckedCheckboxIcon />
            <Box sx={styles.text}>
                {subTaskCompleted}/{totalSubTasks}
            </Box>
        </Box>
    );
};

export const getRiskIconAndHover = (priority: AnnualWheelPriority, translateString: any) => {
    switch (priority) {
        case 1:
            return { icon: reallyLowRiskIcon, iconHoverText: translateString("veryLow") };
        case 2:
            return { icon: lowRiskIcon, iconHoverText: translateString("low") };
        case 3:
            return { icon: moderateRiskIcon, iconHoverText: translateString("moderate") };
        case 4:
            return { icon: highRiskIcon, iconHoverText: translateString("high") };
        case 5:
            return { icon: reallyHighRiskIcon, iconHoverText: translateString("veryHigh") };
        default:
            return { icon: <></>, iconHoverText: "" };
    }
};

export function getRiskIcon(priority: AnnualWheelPriority, translateString: any) {
    const iconInformation = getRiskIconAndHover(priority, translateString);

    return <DotLegalTooltip text={iconInformation.iconHoverText}>{iconInformation.icon}</DotLegalTooltip>;
}

function Task(props: ITaskProps) {
    const styles = useTaskStyles(props.taskStatus, props.isOverdue);
    const ref: React.RefObject<HTMLDivElement> = createRef();
    const location = useLocation();

    const [showTooltip, setShowTooltip] = useState(false);

    const { translateString, translateDate } = useTranslation();
    const { getProcessingActivityValidationUrl } = useUrlProvider();
    const { platformFeatures: platform } = usePlanContext();

    const getTaskLinkUrl = () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("taskId", props.taskId);
        return `?${searchParams.toString()}`;
    };

    useLayoutEffect(() => {
        if (ref?.current) {
            if (ref.current!.offsetHeight < ref.current!.scrollHeight) {
                setShowTooltip(true);
            } else {
                setShowTooltip(false);
            }
        }
    }, [ref]);

    return (
        <DotLegalPaper sx={styles.root}>
            <Box
                sx={(_) => ({
                    display: "flex",
                    width: "100%",
                    height: "100%",
                    minHeight: "55px",
                })}
            >
                <Box
                    sx={(theme) => ({
                        backgroundColor: getTaskColor(props.isReadyForApproval ? TaskRawStateEnum.ReadyForApproval : props.taskStatus, theme, props.isOverdue),
                        marginRight: theme.spacing(2),
                        borderRadius: "12px",
                        paddingRight: "6px",
                    })}
                />

                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, "& > .MuiBox-root": { marginTop: "1px", marginBottom: "2px" } }}>
                    <TaskHeader {...props} />
                    {props.taskName && (
                        <DotLegalHeader fontWeight={600} marginBottom={0.5} color="primary" headerStyle={"extraSmall"}>
                            {props.processingActivtiyId !== null ? (
                                <DotLegalLink linkColor="primary" to={getProcessingActivityValidationUrl(props.processingActivtiyId!)}>
                                    {props.taskName}
                                </DotLegalLink>
                            ) : (
                                <Box
                                    sx={(theme) => ({
                                        color: theme.palette.primary.main,
                                    })}
                                    id="test"
                                >
                                    {props.taskName}
                                </Box>
                            )}
                        </DotLegalHeader>
                    )}
                    {props.description && (
                        <Box>
                            <DotLegalTooltip
                                text={
                                    isNullOrWhitespace(props.description) ? undefined : (
                                        <MentionInputRender value={props.description ?? ""} linkColor={"white"} />
                                    )
                                }
                                disableHoverlistener={!showTooltip}
                            >
                                <Box ref={ref} sx={styles.description}>
                                    <MentionInputRender value={props.description} />
                                </Box>
                            </DotLegalTooltip>
                        </Box>
                    )}
                    {platform.annualWheelComplianceArea && props.areas && (
                        <Box sx={{ display: "flex", columnGap: 1, flexWrap: "wrap" }}>
                            {props.areas?.map((x) => (
                                <ComplianceAreaWrapper key={x.id} complianceArea={x.name} />
                            ))}
                        </Box>
                    )}
                    {props.businessAreas.length > 0 && (
                        <Box sx={styles.businessAreasContainer}>
                            {props.businessAreas.map((ba) => {
                                return (
                                    <Box key={ba.id}>
                                        <DotLegalChip value={ba.name} color={ba.color} size="small" />
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    <TaskFooter {...props} />
                </Box>
            </Box>
        </DotLegalPaper>
    );
}

export default Task;
